<template>
	<div :class="['px-4', giftTo ? 'pt-8 pb-24' : 'py-8']">
		<Card class="mb-8" v-if="application.isOwner">
			<div class="space-y-8">
				<div class="flex gap-2 items-center">
					<NuxtImg
						class="w-[74px] shrink-0"
						:src="`/icons/${props.productType === 'for_sale' ? 'happy-bag' : 'happy-blue-bag'}.svg`"
						:alt="
							props.productType === 'for_sale'
								? 'Adicione produtos à sua loja!'
								: 'Adicione recompensas à sua loja!'
						"
					/>
					<h3 class="text-brand-neutral-6 text-xl font-bold text-center">
						{{
							props.productType === 'for_sale'
								? 'Adicione produtos à sua loja!'
								: `Adicione recompensas 
à sua loja!`
						}}
					</h3>
				</div>
				<Button :href="`/applications/${application._id}/products/create?productType=${props.productType}`">
					<span class="fi fi-sr-add text-2xl flex"></span>
					{{ props.productType === 'for_sale' ? 'Adicionar produto' : 'Adicionar recompensa' }}
				</Button>
			</div>
		</Card>
		<Card class="mb-6" v-if="giftTo">
			<p class="text-center">
				<b>Premiar {{ giftTo.firstName }} {{ giftTo.lastName }} com pontos de fidelidade</b>, selecione os
				produtos adquiridos.
			</p>
		</Card>
		<div v-if="products.results.length === 0" class="text-center">
			<h3 class="mb-4 text-brand-neutral-7 font-bold text-base">
				{{ productType === ProductType.FOR_SALE ? 'Catálogo vazio' : 'Sem recompensas' }}
			</h3>
			<p v-if="application.isOwner" class="text-brand-neutral-5 text-sm">
				{{
					productType === ProductType.FOR_SALE
						? 'Ops! Catálogo vazio. Adicione um item para começar a enviar pontos de fidelidade.'
						: 'Adicione recompensas para resgate.'
				}}
			</p>
			<p v-if="!application.isOwner" class="text-brand-neutral-5 text-sm">
				{{
					productType === ProductType.FOR_SALE
						? 'O lojista ainda não adicionou os produtos em sua loja, tente voltar mais tarde.'
						: 'Nenhuma recompensa disponível no momento. tente voltar mais tarde..'
				}}
			</p>
			<NuxtImg v-if="!application.isOwner" class="mt-6 w-40 mx-auto" src="/icons/sad-mobile.svg" />
		</div>
		<div ref="productsContainerRef" v-if="products.results.length > 0">
			<h3 class="text-brand-neutral-6 text-base font-bold">
				{{ props.productType === ProductType.FOR_SALE ? 'Produtos' : 'Recompensas' }}
			</h3>
			<div
				v-for="product in products.results"
				:key="product._id"
				:class="['border-b-2 pb-4', product.type === ProductType.REWARD ? 'py-8' : 'py-4']"
			>
				<ProductItem @click="() => !giftTo && onOpenProduct(product)" :product="product" />
				<div v-if="application.isOwner && !giftTo" class="flex gap-4 mt-6">
					<Button
						:href="`/applications/${application._id}/products/${product._id}/edit`"
						class="text-brand-neutral-7"
						variant="outline"
					>
						<span class="fi fi-br-file-edit flex text-2xl"></span>
						Editar</Button
					>
					<Button @click="productSelectedForDelete = product" class="w-fit" variant="danger">
						<span class="fi fi-sr-trash flex text-2xl"></span>
					</Button>
				</div>
				<div class="mt-4" v-if="giftTo">
					<Button v-if="!productsSelected[product._id]" @click="onSelectProduct(product)"> Adicionar </Button>
					<div
						class="flex px-6 py-3.5 items-center justify-between bg-brand-neutral-3 rounded-xl text-brand-7"
						v-if="productsSelected[product._id]"
					>
						<Button class="w-6 h-6 !p-0" @click="onDecrementProduct(product)" variant="transparent">
							<span class="fi fi-sr-minus-small flex text-2xl"></span>
						</Button>
						<input
							class="bg-transparent w-12 text-center font-semibold text-base"
							v-model="productsSelected[product._id]"
							type="number"
							max="999"
							maxlength="3"
							min="1"
						/>
						<Button class="h-6 w-6 !p-0" variant="transparent" @click="onIncrementProduct(product)">
							<span class="fi fi-sr-plus-small flex text-2xl"></span>
						</Button>
					</div>
				</div>
			</div>
			<ProductModal
				v-if="productOpened && !application.isSeller"
				:product="productOpened"
				@close="productOpened = null"
			/>
			<DeleteItemModal
				v-if="productSelectedForDelete"
				:title="productSelectedForDelete.name"
				:image="productSelectedForDelete.image"
				@close="productSelectedForDelete = null"
				@delete="() => onDeleteProduct(productSelectedForDelete!._id)"
			/>
			<div
				v-if="giftTo && productsSelectedCount > 0"
				class="fixed bottom-0 flex gap-2 bg-brand-neutral-1 border-t border-brand-neutral-3 right-0 left-0 p-4 items-end md:left-1/2 md:-translate-x-1/2 md:w-96"
			>
				<div class="h-max flex flex-col items-center">
					<p class="text-brand-neutral-4 font-medium text-xs w-max">Itens selecionados:</p>
					<p class="text-center text-brand-neutral-7 font-bold text-2xl">
						{{ productsSelectedCount }}
					</p>
				</div>
				<Button @click="onCreateTransaction" :isLoading="isProcessingTransaction"
					>Confirmar {{ productType === ProductType.FOR_SALE ? 'Venda' : 'Resgate' }}</Button
				>
			</div>
		</div>
		<UsageLimitModal v-if="isUsageLimitModalOpen" :plan="user?.plan!" @close="isUsageLimitModalOpen = false" />
	</div>
</template>
<script setup lang="ts">
const productsContainerRef = ref<HTMLElement | null>(null)

interface IProductItem extends IProduct {
	quantity: number
}

const { user } = useUserStore()
const { $api } = useNuxtApp()
const alertsStore = useAlertsStore()

const props = defineProps<{
	application: IApplication
	productType: ProductType
	giftTo?: IWallet
	userBalance?: number
	isCustomer?: boolean
}>()

const products = ref<IPaginationResults<IProductItem>>({
	results: [],
	hasNext: false,
	hasPrevious: false,
})
const productOpened = ref<IProduct | null>(null)
const productSelectedForDelete = ref<IProduct | null>(null)
/**
 * [id] -> [quantity]
 */
const productsSelected = ref<{ [key: string]: number }>({})
const isProcessingTransaction = ref(false)
const isUsageLimitModalOpen = ref(false)
const isLoadingProducts = ref(false)

products.value = await useApi(`/applications/${props.application._id}/products?type=${props.productType}&limit=6`)

const onOpenProduct = (product: IProduct) => {
	productOpened.value = product
}

const checkCustomerBalance = (product: IProduct) => {
	const balance = props.userBalance || 0
	const subtract = product.points * ((productsSelected.value[product._id] || 0) + 1)

	if (product.type === ProductType.REWARD && balance < subtract) {
		alertsStore.addAlert({
			title: `Saldo insuficiente, o cliente precisa de mais ${subtract - balance} pontos`,
			variant: 'danger',
		})

		return false
	}

	return true
}

const onDeleteProduct = async (productId: string) => {
	try {
		await $api(`/products/${productId}`, {
			method: 'DELETE',
		})

		products.value.results = products.value.results.filter(product => product._id !== productId)

		alertsStore.addAlert({
			title: 'Produto removido com sucesso',
			variant: 'success',
		})

		productSelectedForDelete.value = null
	} catch (error) {
		/**
		 * @todo Log to sentry
		 */
		console.error(error)

		alertsStore.addAlert({
			title: 'Erro ao remover produto',
			variant: 'danger',
		})
	}
}

const onSelectProduct = (product: IProduct) => {
	if (!checkCustomerBalance(product)) {
		return
	}

	productsSelected.value[product._id] = 1
}

const onIncrementProduct = (product: IProduct) => {
	if (!checkCustomerBalance(product)) {
		return
	}

	productsSelected.value[product._id]++
}

const onDecrementProduct = (product: IProduct) => {
	if (productsSelected.value[product._id] === 1) {
		delete productsSelected.value[product._id]
		return
	}

	productsSelected.value[product._id]--
}

const onCreateTransaction = async () => {
	if (isProcessingTransaction.value) {
		return
	}

	isProcessingTransaction.value = true

	try {
		if (!productsSelectedCount.value) {
			alertsStore.addAlert({
				title: 'Selecione ao menos um produto',
				variant: 'danger',
			})

			isProcessingTransaction.value = false
			return
		}

		if (
			!props.isCustomer &&
			user!.subscription!.usage[PlanPermissions.CUSTOMERS] >= user!.plan!.maxUsage[PlanPermissions.CUSTOMERS]
		) {
			isUsageLimitModalOpen.value = true
			isProcessingTransaction.value = false
			return
		}

		if (props.productType === ProductType.REWARD) {
			const subtract = Object.entries(productsSelected.value).reduce((acc, [id, quantity]) => {
				const product = products.value.results.find(product => product._id === id)
				return acc + (product?.points || 0) * quantity
			}, 0)

			const balance = props.userBalance || 0

			if (subtract > balance) {
				alertsStore.addAlert({
					title: `Saldo insuficiente, o cliente precisa de mais ${subtract - balance} pontos`,
					variant: 'danger',
				})

				isProcessingTransaction.value = false
				return
			}
		}

		const response = await $api('/transactions', {
			method: 'POST',
			body: {
				user: props.giftTo?.id,
				application: props.application._id,
				products: Object.entries(productsSelected.value).map(([id, quantity]) => ({
					entity: id,
					entityType: props.productType === ProductType.FOR_SALE ? 'product' : 'reward',
					quantity,
				})),
			},
		})

		await navigateTo(`/account/transactions/${response._id}?displayMessage=true`)
	} catch (error: any) {
		if (error.message === 'You do not have enough points.') {
			return alertsStore.addAlert({
				title: 'Saldo insuficiente',
				variant: 'danger',
			})
		}

		console.error(error)

		/**
		 * @todo Log to sentry
		 */
		alertsStore.addAlert({
			title: 'Erro ao processar',
			variant: 'danger',
		})
	}

	isProcessingTransaction.value = false
}

const productsSelectedCount = computed(() => {
	return Object.values(productsSelected.value).reduce((acc, quantity) => acc + quantity, 0)
})

const fetchProducts = async () => {
	if (isLoadingProducts.value || !products.value.hasNext) {
		return
	}

	isLoadingProducts.value = true

	try {
		const newProducts = await $api(
			`/applications/${props.application._id}/products?type=${props.productType}&limit=6${
				products.value.hasNext ? `&next=${products.value.next}` : ''
			}`
		)
	
		products.value.results.push(...newProducts.results)
		products.value.next = newProducts.next
		products.value.hasNext = newProducts.hasNext
		products.value.hasPrevious = newProducts.hasPrevious
	} catch (error) {
		console.error(error)
	}

	isLoadingProducts.value = false
}

const handleScroll = async () => {
	const container = productsContainerRef.value

	if (container) {
		const { scrollTop, scrollHeight, clientHeight } = container

		if (scrollTop + clientHeight >= scrollHeight - 10) {
			await fetchProducts()
		}
	}
}

onMounted(() => {
	window.addEventListener('scroll', handleScroll, false)
})

onUnmounted(() => {
	window.removeEventListener('scroll', handleScroll, false)
})
</script>
